import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SearchableDropdown from '../components/SearchableDropdown';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface League {
  id: string;
  name: string;
  season: string;
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  leagueId?: string;
  manager?: { id: string; firstName: string; lastName: string };
}

interface TeamJoinRequest {
  id: string;
  teamId: string;
  leagueId?: string;
  status: 'pending' | 'approved' | 'rejected';
  message?: string;
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  team?: { id: string; name: string; teamNumber: string };
  league?: { id: string; name: string };
}

const SelectTeam: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [myRequests, setMyRequests] = useState<TeamJoinRequest[]>([]);

  useEffect(() => {
    if (user?.profileType !== 'player') {
      navigate('/dashboard');
      return;
    }
    fetchLeagues();
    fetchMyRequests();
  }, [user, navigate]);

  useEffect(() => {
    if (selectedLeagueId) {
      fetchTeamsForLeague(selectedLeagueId);
    } else {
      setTeams([]);
      setSelectedTeamId('');
    }
  }, [selectedLeagueId]);

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues', { params: { limit: '100' } });
      const leaguesData = response.data.leagues || response.data;
      setLeagues(leaguesData.map((l: any) => ({ id: l.id, name: l.name })));
    } catch (error) {
      console.error('Error fetching leagues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamsForLeague = async (leagueId: string) => {
    try {
      const response = await api.get('/teams', { params: { limit: '100' } });
      const allTeams = response.data.teams || response.data;
      const leagueTeams = allTeams.filter((t: Team) => t.leagueId === leagueId);
      setTeams(leagueTeams);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setError('Failed to load teams');
    }
  };

  const fetchMyRequests = async () => {
    try {
      const response = await api.get('/team-join-requests/my-requests');
      setMyRequests(response.data);
    } catch (error) {
      console.error('Error fetching my requests:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedTeamId) {
      setError('Please select a team');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/team-join-requests', {
        teamId: selectedTeamId,
        leagueId: selectedLeagueId || undefined,
        message: message.trim() || undefined
      });
      alert('Team join request submitted! The team manager will review your request.');
      setSelectedLeagueId('');
      setSelectedTeamId('');
      setMessage('');
      fetchMyRequests();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="badge badge-success">Approved</span>;
      case 'rejected':
        return <span className="badge badge-danger">Rejected</span>;
      case 'pending':
        return <span className="badge badge-warning">Pending</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container" style={{ maxWidth: '800px' }}>
      <h1>Select a Team to Join</h1>
      <p style={{ color: '#757575', marginBottom: '30px' }}>
        Choose a league and team you'd like to join. Your request will be sent to the team manager for approval.
      </p>

      {myRequests.length > 0 && (
        <div className="card" style={{ marginBottom: '30px' }}>
          <h2>My Team Join Requests</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Team</th>
                <th>League</th>
                <th>Status</th>
                <th>Requested</th>
              </tr>
            </thead>
            <tbody>
              {myRequests.map((request) => (
                <tr key={request.id}>
                  <td>
                    {request.team ? (
                      <Link to={`/teams/${request.teamId}`} style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}>
                        {request.team.name} ({request.team.teamNumber})
                      </Link>
                    ) : (
                      'Unknown Team'
                    )}
                  </td>
                  <td>{request.league ? request.league.name : '-'}</td>
                  <td>{getStatusBadge(request.status)}</td>
                  <td>{new Date(request.requestedAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h2>Request to Join a Team</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>League *</label>
            <SearchableDropdown
              label=""
              options={leagues
                .filter(l => l && l.id && l.name)
                .map(l => ({ id: l.id, label: l.name || 'Unknown League', subtitle: '' }))}
              value={selectedLeagueId}
              onChange={setSelectedLeagueId}
              placeholder="Select a league"
            />
          </div>

          {selectedLeagueId && (
            <div className="form-group">
              <label>Team *</label>
              {teams.length === 0 ? (
                <p style={{ color: '#757575' }}>No teams available in this league.</p>
              ) : (
                <SearchableDropdown
                  label=""
                  options={teams
                    .filter(t => t && t.id && t.name)
                    .map(t => ({ 
                      id: t.id, 
                      label: `${t.name || 'Unknown'} (${t.teamNumber || 'N/A'})`,
                      subtitle: t.manager ? `Manager: ${t.manager.firstName} ${t.manager.lastName}` : undefined
                    }))}
                  value={selectedTeamId}
                  onChange={setSelectedTeamId}
                  placeholder="Select a team"
                />
              )}
            </div>
          )}

          <div className="form-group">
            <label>Message (Optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message to the team manager..."
              rows={4}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #E0E0E0' }}
            />
          </div>

          {error && <div className="error">{error}</div>}

          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%' }}
            disabled={submitting || !selectedTeamId}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Skip for Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectTeam;

